import React, { useState } from 'react';
import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { outOfLessonCol } from '../../lib/firestore';
import { FOUND_REASONS } from './_shared';

/**
 * Marking an Out Of Lesson pupil found, shared by the Out Of Lesson page and
 * the Home panel so the two screens offer the same reason list and the same
 * button. They used to differ ("Safe" and a free-text comment on Home), so
 * staff met two words and two forms for one action.
 *
 * The reason and error state live with the caller, not the row: the listener
 * drops a row the moment the local write lands and a refusal brings it back as
 * a fresh row, so an error held inside the row would be lost with it.
 */
export function useOolMarkFound(me: { uid: string; name: string }, onFound?: () => void) {
  const [reasonFor, setReasonFor] = useState<Record<string, string>>({});
  const [rowErr, setRowErr] = useState<Record<string, string>>({});

  const markFound = (id: string) => {
    setRowErr((prev) => { const { [id]: _drop, ...rest } = prev; return rest; });
    // Not awaited: the row leaves the list as soon as the local write lands.
    // foundByUid makes the resolution attributable to an account, not just a
    // name — and the rules require it to match the caller.
    updateDoc(doc(outOfLessonCol, id), {
      status: 'found', foundByUid: me.uid, foundByName: me.name,
      foundAt: serverTimestamp(), foundReason: reasonFor[id] || null,
    }).catch(() => {
      // Swallowing this left the row in place with no explanation, so the staff
      // member believed a missing child had been accounted for.
      setRowErr((prev) => ({ ...prev, [id]: 'Could not mark found — refresh and try again.' }));
    });
    onFound?.();
  };

  return {
    reasonFor,
    setReason: (id: string, reason: string) => setReasonFor((prev) => ({ ...prev, [id]: reason })),
    rowErr,
    markFound,
  };
}

export type OolMarkFound = ReturnType<typeof useOolMarkFound>;

/** The reason list and Found button for one record, plus its error line.
 *  Sits inside a `flex-wrap` row: the error takes a line of its own. */
export function OolFoundControls({ id, studentName, found }: {
  readonly id: string;
  readonly studentName: string;
  readonly found: OolMarkFound;
}) {
  return (
    <>
      <span className="flex items-center gap-2">
        <select
          className="input !py-1 text-xs"
          value={found.reasonFor[id] ?? ''}
          onChange={(e) => found.setReason(id, e.target.value)}
          aria-label={`Reason ${studentName} was found`}
        >
          <option value="">Reason (optional)</option>
          {FOUND_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <button type="button" onClick={() => found.markFound(id)} className="btn-secondary !py-1 text-xs">Found</button>
      </span>
      {found.rowErr[id] && (
        <p role="alert" className="w-full text-xs font-medium text-red-700">{found.rowErr[id]}</p>
      )}
    </>
  );
}
